/**
 * Firebase-backed {@link PortalServices} — the ONLY module that imports
 * `firebase/*` directly (besides env). It builds the app + handles via
 * @djgg/cloud's `createFirebaseApp`, wraps the @djgg/creations repos over a
 * the Supabase data layer (PostgREST reads + Realtime subscriptions — the port
 * can't express live reads), wires `firebase/auth` (legacy password sign-in +
 * Google account creation/sign-in + custom-claim roles), and
 * `firebase/functions` httpsCallables.
 *
 * Everything else in the app takes the {@link PortalServices} interface, so this
 * concrete wiring is swappable for a fake in tests.
 */
import { createFirebaseApp } from '@djgg/cloud';
import { createClient as createSupabaseClient } from '@supabase/supabase-js';
import { SupabaseDocStore } from '@djgg/db/browser';
import {
  buildSupabaseSubscriptions,
  fetchAssetVariants,
  fetchMyBadgeProgress,
  fetchPublicStats,
  fetchPublicUserProfileSummary,
  fetchGameFans,
  fetchGameRecentPlayers,
  fetchPlatformLeaderboard,
} from './supabase.js';
import {
  COMPOSE_PLAN_CLIENT_TIMEOUT_MS,
  COMPOSED_ADAPTATION_CLIENT_TIMEOUT_MS,
  COMPOSED_RULE_CLIENT_TIMEOUT_MS,
  COMPOSED_SPEC_CLIENT_TIMEOUT_MS,
  COMPOSED_TEMPLATE_CLIENT_TIMEOUT_MS,
  SETTINGS_SCHEMA_VERSION,
  CreationsRepo,
  LikesRepo,
  ViewsRepo,
  VersionsRepo,
  BuildJobsRepo,
  CreatorProfilesRepo,
  PlatformSettingsRepo,
  COLLECTIONS,
  SUBCOLLECTIONS,
  type ActionLogEntry,
  type Comment,
  type Creation,
  type CreationVersion,
  type CreatorProfile,
  type BuildJob,
  type LlmRequestDoc,
  type Role,
} from '@djgg/creations';
import type { DocStorePort, Unsubscribe } from '@djgg/core';
import { LeaderboardSnapshotReader } from '@djgg/leaderboard';
import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut as fbSignOut,
  type Auth,
  type User,
} from 'firebase/auth';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';
import { createRecordedMatchClient } from './recordedMatch.js';
import { callWithDatabaseAdmissionRetry } from './callableRetry.js';
import {
  firebaseConfig,
  useEmulators,
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  PORTAL_EMULATORS,
  PORTAL_FUNCTIONS_EMULATOR_PORT,
  FUNCTIONS_REGION,
} from '../platform/env.js';
import type { PortalUser } from '../auth/model.js';
import type {
  PortalServices,
  Repos,
  Subscriptions,
  AuthApi,
  Callables,
  RequestBuildResult,
  AssetVariant,
} from './types.js';

/** Map a Firebase `User` + its claims into the portal's user shape. */
async function toPortalUser(user: User | null, fns?: Functions): Promise<PortalUser | null> {
  if (!user) return null;
  let role: Role | undefined;
  try {
    let token = await user.getIdTokenResult();
    // Supabase maps the JWT `role` claim to a Postgres role; a role-less token
    // runs as anon and loses access to the user's OWN rows. Stamp the baseline
    // 'authenticated' claim once (self-service callable) and refresh.
    if (!token.claims.role && fns) {
      await httpsCallable(fns, 'ensureRoleClaimFn')({});
      await user.getIdToken(/* forceRefresh */ true);
      token = await user.getIdTokenResult();
    }
    const claimRole = token.claims.role;
    if (claimRole === 'creator' || claimRole === 'superadmin') role = claimRole;
  } catch {
    // A token read failure leaves the user role-less (browse-only) rather than
    // signed out — the rules still gate every privileged write.
  }
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    role,
    isAnonymous: user.isAnonymous,
  };
}

/**
 * Wrap a subscription's `onError` so a denied/failed Firestore listener NEVER
 * becomes an uncaught error: the caller's handler runs if present, and we always
 * log a warning through the SDK's own channel. Returns the onError argument
 * the Supabase subscription layer expects.
 */
function snapshotError(
  label: string,
  onError?: (error: unknown) => void,
): (error: unknown) => void {
  return (error: unknown) => {
    // Degrade visibly — a permission-denied listener should not spam the console
    // with an uncaught rejection; the page's onError decides how to render it.
    if (onError) onError(error);
    else console.warn(`[portal] subscription '${label}' error:`, error);
  };
}

/**
 * Best-effort creator-profile STUB upsert on sign-in — makes every real account
 * discoverable in the collaborator invite picker (`creatorProfiles` doubles as
 * the platform user directory). Create-only: an existing profile (and its
 * handle) is NEVER overwritten, and `gamesCount` is omitted because the rules
 * reject it on owner create (server-owned). All failures are swallowed —
 * offline/rules races must never break the auth path.
 */
const profileStubChecked = new Set<string>();
async function ensureProfileStub(store: DocStorePort, user: PortalUser): Promise<void> {
  if (profileStubChecked.has(user.uid)) return;
  profileStubChecked.add(user.uid);
  try {
    const path = `${COLLECTIONS.creatorProfiles}/${user.uid}`;
    if (await store.getDoc(path)) return;
    const handle =
      user.email?.split('@')[0]?.trim() || user.displayName?.trim() || user.uid.slice(0, 8);
    // joinedAt is server-owned (RLS column grants exclude it) — the DB default
    // stamps it; sending it 42501'd every first sign-in (review finding).
    await store.setDoc(path, { handle, bio: '', avatarKey: '' });
  } catch {
    // Best-effort only; allow a retry on the next auth event of this session.
    profileStubChecked.delete(user.uid);
  }
}

/** Build the auth surface. */
function buildAuth(auth: Auth, store: DocStorePort, fns: Functions): AuthApi {
  return {
    onChange(cb) {
      const unsub = onAuthStateChanged(auth, (user) => {
        void toPortalUser(user, fns).then((portalUser) => {
          cb(portalUser);
          // Fire-and-forget: the directory stub must never delay or fail auth.
          if (portalUser && !portalUser.isAnonymous) void ensureProfileStub(store, portalUser);
        });
      });
      return unsub as Unsubscribe;
    },
    async signInEmail(email, password) {
      await signInWithEmailAndPassword(auth, email, password);
    },
    async signInGoogle() {
      // REDIRECT, not popup. A popup is unusable on the surfaces that matter
      // most here: iOS Safari blocks the `window.open` the SDK reaches after
      // its async pre-work (`auth/popup-blocked`), and an installed home-screen
      // web app sends the popup to Safari, whose storage container the app can
      // never read back. The redirect leaves this document — the outcome comes
      // back through `completeRedirectSignIn`.
      await signInWithRedirect(auth, new GoogleAuthProvider());
    },
    async completeRedirectSignIn() {
      // Resolves to null on an ordinary load (nothing pending) and THROWS when
      // the provider round-trip failed — the caller renders that error.
      await getRedirectResult(auth);
    },
    async signOut() {
      await fbSignOut(auth);
    },
    async refreshClaims() {
      // (claim stamping happens inside toPortalUser)
      const user = auth.currentUser;
      if (!user) return null;
      await user.getIdToken(true);
      return toPortalUser(user, fns);
    },
  };
}

/**
 * Build the callable-functions surface.
 *
 * The httpsCallable NAMES here MUST match the DEPLOYED function export ids in
 * services/functions (`functions.yaml` endpoints) — the deploy exports every
 * callable with an `Fn` suffix (`createCreationFn`, `likeCreationFn`, …). A
 * mismatch 404s silently: the wizard, iterate build, like/unlike, admin role
 * management and admin delete all fail with an opaque `internal` error. The
 * tools/verify emulator e2e drives one of these end-to-end so a rename can never
 * split the seam again.
 */
function buildCallables(fns: Functions): Callables {
  const call = <TIn, TOut>(name: string, options?: { timeoutMs?: number }) => {
    // The firebase SDK's default callable timeout is 70s — fine for the quick
    // mutations, fatally short for the long-running ones (asset generation
    // runs 3 image-model calls + postprocess server-side, measured ~250s
    // against the function's own 300s ceiling). Callers that outlive the
    // default pass an explicit timeout.
    const fn = httpsCallable<TIn, TOut>(fns, name, options?.timeoutMs ? { timeout: options.timeoutMs } : undefined);
    return async (input: TIn): Promise<TOut> => (
      await callWithDatabaseAdmissionRetry(() => fn(input))
    ).data;
  };
  const createCreation = call<
    Parameters<Callables['createCreation']>[0],
    Awaited<ReturnType<Callables['createCreation']>>
  >('createCreationFn');
  const updatePlatformSettings = call<
    Parameters<Callables['updatePlatformSettings']>[0],
    Awaited<ReturnType<Callables['updatePlatformSettings']>>
  >('updatePlatformSettingsFn');
  const getPlatformSettings = call<
    Parameters<Callables['getPlatformSettings']>[0],
    Awaited<ReturnType<Callables['getPlatformSettings']>>
  >('getPlatformSettingsFn');
  const requestBuild = call<Parameters<Callables['requestBuild']>[0], RequestBuildResult>(
    'requestBuildFn',
  );
  const requestAssetPublish = call<
    Parameters<Callables['requestAssetPublish']>[0],
    Awaited<ReturnType<Callables['requestAssetPublish']>>
  >('requestAssetPublishFn');
  const previewAssetPublish = call<
    Parameters<Callables['previewAssetPublish']>[0],
    Awaited<ReturnType<Callables['previewAssetPublish']>>
  >('previewAssetPublishFn');
  const forkCreation = call<
    Parameters<Callables['forkCreation']>[0],
    Awaited<ReturnType<Callables['forkCreation']>>
  >('forkCreationFn');
  const cancelBuild = call<
    Parameters<Callables['cancelBuild']>[0],
    Awaited<ReturnType<Callables['cancelBuild']>>
  >('cancelBuildFn');
  const adminRequeueBuild = call<
    Parameters<Callables['adminRequeueBuild']>[0],
    Awaited<ReturnType<Callables['adminRequeueBuild']>>
  >('adminRequeueBuildFn');
  const adminFailBuild = call<
    Parameters<Callables['adminFailBuild']>[0],
    Awaited<ReturnType<Callables['adminFailBuild']>>
  >('adminFailBuildFn');
  const setActiveVersion = call<
    Parameters<Callables['setActiveVersion']>[0],
    { creationId: string; activeVersion: number; activeBundleUrl: string }
  >('setActiveVersionFn');
  const like = call<{ creationId: string }, void>('likeCreationFn');
  const unlike = call<{ creationId: string }, void>('unlikeCreationFn');
  const postComment = call<
    Parameters<Callables['postComment']>[0],
    Awaited<ReturnType<Callables['postComment']>>
  >('postCommentFn');
  const deleteComment = call<
    Parameters<Callables['deleteComment']>[0],
    Awaited<ReturnType<Callables['deleteComment']>>
  >('deleteCommentFn');
  const createMatchInvite = call<
    Parameters<Callables['createMatchInvite']>[0],
    Awaited<ReturnType<Callables['createMatchInvite']>>
  >('createMatchInviteFn');
  const acceptMatchInvite = call<
    Parameters<Callables['acceptMatchInvite']>[0],
    Awaited<ReturnType<Callables['acceptMatchInvite']>>
  >('acceptMatchInviteFn');
  const cancelMatchInvite = call<
    Parameters<Callables['cancelMatchInvite']>[0],
    Awaited<ReturnType<Callables['cancelMatchInvite']>>
  >('cancelMatchInviteFn');
  const updateMyProfile = call<
    Parameters<Callables['updateMyProfile']>[0],
    Awaited<ReturnType<Callables['updateMyProfile']>>
  >('updateMyProfileFn');
  const recordGameFinish = call<
    Parameters<Callables['recordGameFinish']>[0],
    Awaited<ReturnType<Callables['recordGameFinish']>>
  >('recordGameFinishFn');
  const heartbeatPresence = call<
    Parameters<Callables['heartbeatPresence']>[0],
    Awaited<ReturnType<Callables['heartbeatPresence']>>
  >('heartbeatPresenceFn');
  const postChatMessage = call<
    Parameters<Callables['postChatMessage']>[0],
    Awaited<ReturnType<Callables['postChatMessage']>>
  >('postChatMessageFn');
  const deleteChatMessage = call<
    Parameters<Callables['deleteChatMessage']>[0],
    Awaited<ReturnType<Callables['deleteChatMessage']>>
  >('deleteChatMessageFn');
  const getCreationInsights = call<
    Parameters<Callables['getCreationInsights']>[0],
    Awaited<ReturnType<Callables['getCreationInsights']>>
  >('getCreationInsightsFn');
  const getBuildTrace = call<
    Parameters<Callables['getBuildTrace']>[0],
    Awaited<ReturnType<Callables['getBuildTrace']>>
  >('getBuildTraceFn');
  const requestBuildDraftPreviewLaunch = call<
    Parameters<Callables['requestBuildDraftPreviewLaunch']>[0],
    Awaited<ReturnType<Callables['requestBuildDraftPreviewLaunch']>>
  >('requestBuildDraftPreviewLaunchFn');
  const getAssetGenTrace = call<
    Parameters<Callables['getAssetGenTrace']>[0],
    Awaited<ReturnType<Callables['getAssetGenTrace']>>
  >('getAssetGenTraceFn');
  const getFailedCandidateInspection = call<
    Parameters<Callables['getFailedCandidateInspection']>[0],
    Awaited<ReturnType<Callables['getFailedCandidateInspection']>>
  >('getFailedCandidateInspectionFn');
  const requestCandidateRecheck = call<
    Parameters<Callables['requestCandidateRecheck']>[0],
    Awaited<ReturnType<Callables['requestCandidateRecheck']>>
  >('requestCandidateRecheckFn');
  const cancelCandidateRecheck = call<
    Parameters<Callables['cancelCandidateRecheck']>[0],
    Awaited<ReturnType<Callables['cancelCandidateRecheck']>>
  >('cancelCandidateRecheckFn');
  const getQualityOperations = call<
    Parameters<Callables['getQualityOperations']>[0],
    Awaited<ReturnType<Callables['getQualityOperations']>>
  >('getQualityOperationsFn');
  const initializeQualityControl = call<
    Parameters<Callables['initializeQualityControl']>[0],
    Awaited<ReturnType<Callables['initializeQualityControl']>>
  >('initializeQualityControlFn');
  const updateQualityControl = call<
    Parameters<Callables['updateQualityControl']>[0],
    Awaited<ReturnType<Callables['updateQualityControl']>>
  >('updateQualityControlFn');
  const deleteCreation = call<Parameters<Callables['deleteCreation']>[0], void>(
    'deleteCreationFn',
  );
  const updateCreation = call<
    Parameters<Callables['updateCreation']>[0],
    Awaited<ReturnType<Callables['updateCreation']>>
  >('updateCreationFn');
  const updateGameSettingsRaw = call<
    Parameters<Callables['updateGameSettings']>[0] & { schemaVersion: number },
    Awaited<ReturnType<Callables['updateGameSettings']>>
  >('updateGameSettingsFn');
  // The schema-epoch handshake rides EVERY save (a stale tab must fail loudly
  // rather than silently erase decisions it doesn't know about).
  const updateGameSettings: Callables['updateGameSettings'] = (input) =>
    updateGameSettingsRaw({ ...input, schemaVersion: SETTINGS_SCHEMA_VERSION });
  const addEditor = call<
    Parameters<Callables['addEditor']>[0],
    Awaited<ReturnType<Callables['addEditor']>>
  >('addEditorFn');
  const removeEditor = call<
    Parameters<Callables['removeEditor']>[0],
    Awaited<ReturnType<Callables['removeEditor']>>
  >('removeEditorFn');
  const deleteVersion = call<
    Parameters<Callables['deleteVersion']>[0],
    Awaited<ReturnType<Callables['deleteVersion']>>
  >('deleteVersionFn');
  const recordCreationFeedback = call<
    Parameters<Callables['recordCreationFeedback']>[0],
    Awaited<ReturnType<Callables['recordCreationFeedback']>>
  >('recordCreationFeedbackFn');
  const setUserRole = call<{ email: string; role: Role | null }, { uid: string }>('setUserRoleFn');
  const getAdminStats = call<
    Parameters<Callables['getAdminStats']>[0],
    Awaited<ReturnType<Callables['getAdminStats']>>
  >('getAdminStatsFn');
  // The caller that wins the Gazette lease generates INSIDE its own invocation:
  // two LLM passes, measured 68–80s in prod against the function's 540s
  // ceiling. The SDK's 70s default sat right in the middle of that band, so a
  // finished generation regularly came back to the page as `deadline-exceeded`.
  // Outlast the server by 30s, and let its own answer end the call.
  const getActivitySummary = call<
    Parameters<Callables['getActivitySummary']>[0],
    Awaited<ReturnType<Callables['getActivitySummary']>>
  >('getActivitySummaryFn', { timeoutMs: 570_000 });
  const resolveLlmPromptUrl = call<
    Parameters<Callables['resolveLlmPromptUrl']>[0],
    Awaited<ReturnType<Callables['resolveLlmPromptUrl']>>
  >('resolveLlmPromptUrlFn');
  // Asset studio: real callables (functions enforce membership + attribution +
  // the daily quota). generateAssets is now an ASYNC kickoff — it returns a
  // jobId fast (validate + enqueue), so the default 70s timeout is ample; the
  // studio watches the job over realtime (subscribe.assetGenJobs).
  const generateAssets = call<
    Parameters<Callables['generateAssets']>[0],
    Awaited<ReturnType<Callables['generateAssets']>>
  >('generateAssetsFn');
  // Asset studio: upload creator-supplied PNGs (bring-your-own art) + attach.
  // No model call, but the base64 payload is larger — give it headroom past the
  // function's 120s ceiling.
  const uploadAssets = call<
    Parameters<Callables['uploadAssets']>[0],
    Awaited<ReturnType<Callables['uploadAssets']>>
  >('uploadAssetsFn', { timeoutMs: 130_000 });
  const getConceptAnalysisResume = call<
    Parameters<Callables['getConceptAnalysisResume']>[0],
    Awaited<ReturnType<Callables['getConceptAnalysisResume']>>
  >('getConceptAnalysisResumeFn');
  const saveConceptManualDraft = call<
    Parameters<Callables['saveConceptManualDraft']>[0],
    Awaited<ReturnType<Callables['saveConceptManualDraft']>>
  >('saveConceptManualDraftFn');
  const promoteConceptManualDraft = call<
    Parameters<Callables['promoteConceptManualDraft']>[0],
    Awaited<ReturnType<Callables['promoteConceptManualDraft']>>
  >('promoteConceptManualDraftFn');
  // Concept-first creation: analysis is long-running (reference fetches +
  // several LLM calls, server ceiling 540s) — outlast it like generateAssets.
  const analyzeConcept = call<
    Parameters<Callables['analyzeConcept']>[0],
    Awaited<ReturnType<Callables['analyzeConcept']>>
  >('analyzeConceptFn', { timeoutMs: 570_000 });
  const skipConceptQuestions = call<
    Parameters<Callables['skipConceptQuestions']>[0],
    Awaited<ReturnType<Callables['skipConceptQuestions']>>
  >('skipConceptQuestionsFn', { timeoutMs: 570_000 });
  const updateGameDesign = call<
    Parameters<Callables['updateGameDesign']>[0],
    Awaited<ReturnType<Callables['updateGameDesign']>>
  >('updateGameDesignFn');
  const workshop = call<Parameters<Callables['workshop']>[0], Awaited<ReturnType<Callables['workshop']>>>('workshopFn');
  const attachAsset = call<
    Parameters<Callables['attachAsset']>[0],
    Awaited<ReturnType<Callables['attachAsset']>>
  >('attachAssetFn');
  const attachAnimationSet = call<
    Parameters<Callables['attachAnimationSet']>[0],
    Awaited<ReturnType<Callables['attachAnimationSet']>>
  >('attachAssetFn');
  const manageMediaReview = call<
    Parameters<Callables['manageMediaReview']>[0],
    Awaited<ReturnType<Callables['manageMediaReview']>>
  >('manageMediaReviewFn');
  const reviewAssetVariant = call<
    Parameters<Callables['reviewAssetVariant']>[0],
    Awaited<ReturnType<Callables['reviewAssetVariant']>>
  >('reviewAssetVariantFn');
  const detachAsset = call<
    Parameters<Callables['detachAsset']>[0],
    Awaited<ReturnType<Callables['detachAsset']>>
  >('detachAssetFn');
  // Editors' Pick "run now" / admin override: a full-catalog LLM review, server
  // ceiling 540s — outlast it like analyzeConcept.
  const pickEditorsPicks = call<
    Parameters<Callables['pickEditorsPicks']>[0],
    Awaited<ReturnType<Callables['pickEditorsPicks']>>
  >('pickEditorsPicksFn', { timeoutMs: 550_000 });

  return {
    getPlatformSettings,
    updatePlatformSettings,
    createCreation,
    listBuildPipelines: call<Parameters<Callables['listBuildPipelines']>[0], Awaited<ReturnType<Callables['listBuildPipelines']>>>('listBuildPipelinesFn'),
    publishBuildPipeline: call<Parameters<Callables['publishBuildPipeline']>[0], Awaited<ReturnType<Callables['publishBuildPipeline']>>>('publishBuildPipelineFn'),
    setBuildPipelineHidden: call<Parameters<Callables['setBuildPipelineHidden']>[0], Awaited<ReturnType<Callables['setBuildPipelineHidden']>>>('setBuildPipelineHiddenFn'),
    requestBuild,
    advanceComposedCreate: call<Parameters<Callables['advanceComposedCreate']>[0], Awaited<ReturnType<Callables['advanceComposedCreate']>>>('advanceComposedCreateFn', { timeoutMs: COMPOSED_RULE_CLIENT_TIMEOUT_MS }),
    advanceComposedRevision: call<Parameters<Callables['advanceComposedRevision']>[0], Awaited<ReturnType<Callables['advanceComposedRevision']>>>('advanceComposedRevisionFn', { timeoutMs: COMPOSED_RULE_CLIENT_TIMEOUT_MS }),
    planComposedGame: call<Parameters<Callables['planComposedGame']>[0], Awaited<ReturnType<Callables['planComposedGame']>>>('planComposedGameFn', { timeoutMs: COMPOSE_PLAN_CLIENT_TIMEOUT_MS }),
    synthesizeComposedSpec: call<Parameters<Callables['synthesizeComposedSpec']>[0], Awaited<ReturnType<Callables['synthesizeComposedSpec']>>>('synthesizeComposedSpecFn', { timeoutMs: COMPOSED_SPEC_CLIENT_TIMEOUT_MS }),
    generateComposedTemplate: call<Parameters<Callables['generateComposedTemplate']>[0], Awaited<ReturnType<Callables['generateComposedTemplate']>>>('generateComposedTemplateFn', { timeoutMs: COMPOSED_TEMPLATE_CLIENT_TIMEOUT_MS }),
    adaptComposedTemplate: call<Parameters<Callables['adaptComposedTemplate']>[0], Awaited<ReturnType<Callables['adaptComposedTemplate']>>>('adaptComposedTemplateFn', { timeoutMs: COMPOSED_ADAPTATION_CLIENT_TIMEOUT_MS }),
    generateComposedRule: call<Parameters<Callables['generateComposedRule']>[0], Awaited<ReturnType<Callables['generateComposedRule']>>>('generateComposedRuleFn', { timeoutMs: COMPOSED_RULE_CLIENT_TIMEOUT_MS }),
    requestAssetPublish,
    previewAssetPublish,
    forkCreation,
    cancelBuild,
    adminRequeueBuild,
    adminFailBuild,
    setActiveVersion,
    like: (creationId) => like({ creationId }),
    unlike: (creationId) => unlike({ creationId }),
    postComment,
    deleteComment,
    createMatchInvite,
    acceptMatchInvite,
    cancelMatchInvite,
    updateMyProfile,
    recordGameFinish,
    heartbeatPresence,
    postChatMessage,
    deleteChatMessage,
    getCreationInsights,
    getBuildTrace,
    requestBuildDraftPreviewLaunch,
    getAssetGenTrace,
    getFailedCandidateInspection,
    requestCandidateRecheck,
    cancelCandidateRecheck,
    getQualityOperations,
    initializeQualityControl,
    updateQualityControl,
    deleteCreation,
    updateCreation,
    updateGameSettings,
    addEditor,
    removeEditor,
    deleteVersion,
    recordCreationFeedback,
    setUserRole,
    getAdminStats,
    getActivitySummary,
    resolveLlmPromptUrl,
    generateAssets,
    uploadAssets,
    getConceptAnalysisResume,
    saveConceptManualDraft,
    promoteConceptManualDraft,
    workshop,
    attachAsset,
    attachAnimationSet,
    reviewAssetVariant,
    manageMediaReview,
    detachAsset,
    analyzeConcept,
    skipConceptQuestions,
    updateGameDesign,
    pickEditorsPicks,
  };
}

/**
 * The `bumpView` callable — the TRUSTED path for the sharded view counter. The
 * portal must NEVER write `creations/{id}/viewShards/{n}` directly (rules lock
 * it to server-only); it calls this unauthenticated-OK function, which
 * increments a random shard via the admin SDK. Named to match the deployed
 * `bumpViewFn` export.
 */
function buildBumpView(fns: Functions): (creationId: string) => Promise<void> {
  const bump = httpsCallable<{ creationId: string }, { ok: boolean }>(fns, 'bumpViewFn');
  return async (creationId: string): Promise<void> => {
    await bump({ creationId });
  };
}

/** The trusted engaged-play path (`bumpPlayFn` → `Creation.playsCount`). */
function buildBumpPlay(fns: Functions): (creationId: string) => Promise<void> {
  const bump = httpsCallable<{ creationId: string }, { ok: boolean }>(fns, 'bumpPlayFn');
  return async (creationId: string): Promise<void> => {
    await bump({ creationId });
  };
}

/** The portal session/page-view counter (`trackVisitFn` → `portalSessions`). */
function buildTrackVisit(fns: Functions): (sessionId: string) => Promise<void> {
  const track = httpsCallable<{ sessionId: string }, { ok: boolean }>(fns, 'trackVisitFn');
  return async (sessionId: string): Promise<void> => {
    await track({ sessionId });
  };
}

/**
 * Create the live Firebase-backed portal services. Called ONCE at app boot;
 * `createFirebaseApp` reuses a same-named app so re-entry is safe.
 */
export function createFirebaseServices(): PortalServices {
  const handles = createFirebaseApp(firebaseConfig(), {
    emulators: useEmulators() ? PORTAL_EMULATORS : false,
  });
  // THE data backend (plan 002, post-cutover one-path): Supabase everywhere —
  // dev included (RLS + the publishable key are the gate; ?emu=1 still wires
  // the AUTH + FUNCTIONS emulators for local callable work). The Firestore
  // data fork was deleted after the cutover soak per djgg-one-path.
  const supabase = createSupabaseClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    accessToken: async () => (await handles.auth.currentUser?.getIdToken()) ?? null,
  });
  const docStore: DocStorePort = new SupabaseDocStore(supabase);
  const fns = getFunctions(handles.app, FUNCTIONS_REGION);

  // `createFirebaseApp` wires auth/firestore/rtdb emulators but NOT functions
  // (functions isn't one of its handles). Without this, every callable in DEV /
  // ?emu=1 hits the nonexistent prod endpoint for the demo project and 404s, so
  // the whole create→build→like emulator e2e can't pass. Wire it here.
  if (useEmulators()) {
    connectFunctionsEmulator(fns, PORTAL_EMULATORS.host ?? 'localhost', PORTAL_FUNCTIONS_EMULATOR_PORT);
  }

  const repos: Repos = {
    creations: new CreationsRepo(docStore),
    likes: new LikesRepo(docStore),
    views: new ViewsRepo(docStore),
    versions: new VersionsRepo(docStore),
    buildJobs: new BuildJobsRepo(docStore),
    profiles: new CreatorProfilesRepo(docStore),
    settings: new PlatformSettingsRepo(docStore),
    leaderboard: new LeaderboardSnapshotReader(docStore),
  };

  // View counts go through the TRUSTED bumpView callable — the portal may not
  // write viewShards directly (rules lock it server-only). A direct write throws
  // PERMISSION_DENIED and never moves the count.
  const bumpView = buildBumpView(fns);
  const bumpPlay = buildBumpPlay(fns);
  const trackVisit = buildTrackVisit(fns);

  return {
    repos,
    subscribe: buildSupabaseSubscriptions(supabase),
    auth: buildAuth(handles.auth, docStore, fns),
    callable: buildCallables(fns),
    recordedMatchResult: createRecordedMatchClient(),
    recordView: (creationId) => bumpView(creationId),
    recordPlay: (creationId) => bumpPlay(creationId),
    recordVisit: (sessionId) => trackVisit(sessionId),
    publicStats: () => fetchPublicStats(supabase),
    myBadgeProgress: () => fetchMyBadgeProgress(supabase),
    userProfileSummary: (uid) => fetchPublicUserProfileSummary(supabase, uid),
    gameRecentPlayers: (gameKey) => fetchGameRecentPlayers(supabase, gameKey),
    gameFans: (gameKey) => fetchGameFans(supabase, gameKey),
    platformLeaderboard: (input) => fetchPlatformLeaderboard(supabase, input),
    fetchAssetVariants: (creationId, opts) => fetchAssetVariants(supabase, creationId, opts),
  };
}
